import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrderDetail } from "@/hooks/useOrderDetail";
import { getStatusLabel } from "@/hooks/useOrders";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { User, MapPin, Package, Mail, Phone, MessageSquare, Truck } from "lucide-react";

interface OrderDetailSheetProps {
  ordernumber: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatPrice = (price: number | null): string => {
  if (price === null || price === undefined) return "€ 0,00";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(price);
};

const OrderDetailSheet = ({ ordernumber, open, onOpenChange }: OrderDetailSheetProps) => {
  const { data: orderDetail, isLoading } = useOrderDetail(ordernumber);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
        {isLoading ? (
          <div className="p-6 space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
            <Separator />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : orderDetail ? (
          <>
            {/* Header */}
            <SheetHeader className="p-6 pb-0">
              <div className="flex items-center justify-between">
                <SheetTitle className="text-xl font-bold">
                  Order {orderDetail.orderInfo.ordernumber}
                </SheetTitle>
                {orderDetail.orderInfo.status && (
                  <Badge variant="secondary">
                    {getStatusLabel(orderDetail.orderInfo.status)}
                  </Badge>
                )}
              </div>
              <SheetDescription>
                {orderDetail.orderInfo.createdat && (
                  <>
                    Aangemaakt op{" "}
                    {format(new Date(orderDetail.orderInfo.createdat), "d MMMM yyyy 'om' HH:mm", {
                      locale: nl,
                    })}
                  </>
                )}
              </SheetDescription>
            </SheetHeader>

            {/* Scrollable Content */}
            <ScrollArea className="flex-1 px-6">
              <div className="space-y-6 pb-6">
                {/* Customer Info */}
                <div className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                      Klantgegevens
                    </h3>
                  </div>
                  <Card>
                    <CardContent className="pt-4 space-y-2">
                      <p className="font-medium">
                        {[orderDetail.orderInfo.voornaam_klant, orderDetail.orderInfo.achternaam_klant]
                          .filter(Boolean)
                          .join(" ") || "-"}
                      </p>
                      {orderDetail.orderInfo.emailadres_klant && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="h-3.5 w-3.5" />
                          <a 
                            href={`mailto:${orderDetail.orderInfo.emailadres_klant}`}
                            className="hover:text-foreground transition-colors"
                          >
                            {orderDetail.orderInfo.emailadres_klant}
                          </a>
                        </div>
                      )}
                      {orderDetail.orderInfo.telefoonnummer_klant && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="h-3.5 w-3.5" />
                          <a 
                            href={`tel:${orderDetail.orderInfo.telefoonnummer_klant}`}
                            className="hover:text-foreground transition-colors"
                          >
                            {orderDetail.orderInfo.telefoonnummer_klant}
                          </a>
                        </div>
                      )}
                      {orderDetail.orderInfo.opmerking_klant && (
                        <div className="pt-2 border-t mt-2">
                          <div className="flex items-start gap-2 text-sm">
                            <MessageSquare className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
                            <p className="italic text-muted-foreground">
                              "{orderDetail.orderInfo.opmerking_klant}"
                            </p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Shipping Address */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                      Verzendadres
                    </h3>
                  </div>
                  <Card>
                    <CardContent className="pt-4 space-y-1">
                      <p>
                        {[
                          orderDetail.orderInfo.addressshippingstreet,
                          orderDetail.orderInfo.addressshippingnumber,
                        ]
                          .filter(Boolean)
                          .join(" ") || "-"}
                      </p>
                      <p>
                        {[
                          orderDetail.orderInfo.addressshippingzipcode,
                          orderDetail.orderInfo.addressshippingcity,
                        ]
                          .filter(Boolean)
                          .join(" ") || "-"}
                      </p>
                      {orderDetail.orderInfo.verzendadres_land && (
                        <p className="text-muted-foreground">
                          {orderDetail.orderInfo.verzendadres_land}
                        </p>
                      )}
                      {orderDetail.orderInfo.verzendmethode && (
                        <div className="flex items-center gap-2 pt-2 text-sm">
                          <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                          <span>{orderDetail.orderInfo.verzendmethode}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Products */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                      Producten ({orderDetail.totalItems} items)
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {orderDetail.products.map((product) => (
                      <Card key={product.id}>
                        <CardContent className="p-3">
                          <div className="flex gap-3">
                            {/* Product Image */}
                            {product.image_url ? (
                              <img
                                src={product.image_url}
                                alt=""
                                className="w-16 h-16 object-cover rounded border flex-shrink-0"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="w-16 h-16 bg-muted rounded border flex-shrink-0 flex items-center justify-center">
                                <Package className="h-6 w-6 text-muted-foreground" />
                              </div>
                            )}

                            {/* Product Details */}
                            <div className="flex-1 min-w-0 space-y-1">
                              <p className="font-medium truncate">
                                {product.producttitle || "-"}
                              </p>
                              {product.varianttitle && (
                                <p className="text-sm text-muted-foreground truncate">
                                  {product.varianttitle}
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground font-mono">
                                SKU: {product.sku || product.artikel_code || "-"}
                              </p>
                              <div className="flex flex-wrap gap-1.5 pt-1">
                                {product.customfieldcolor && (
                                  <Badge variant="outline" className="text-xs">
                                    {product.customfieldcolor}
                                  </Badge>
                                )}
                                {product.brand_title && (
                                  <Badge variant="secondary" className="text-xs">
                                    {product.brand_title}
                                  </Badge>
                                )}
                                {product.suppliertitle && (
                                  <Badge variant="outline" className="text-xs text-muted-foreground">
                                    {product.suppliertitle}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Quantity and Price */}
                            <div className="text-right flex-shrink-0">
                              <p className="font-medium">
                                {product.quantityordered || 0}x
                              </p>
                              {product.quantity_shipped !== null && 
                               product.quantity_shipped !== product.quantityordered && (
                                <p className="text-xs text-muted-foreground">
                                  ({product.quantity_shipped}x verzonden)
                                </p>
                              )}
                              <p className="text-sm font-medium mt-1">
                                {formatPrice(product.prijs_incl_btw)}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>

            {/* Footer with Total */}
            <div className="border-t p-6 bg-muted/30">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-lg">Totaal</span>
                <span className="font-bold text-xl">
                  {formatPrice(orderDetail.totalPrice)}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="p-6 text-center text-muted-foreground">
            Order niet gevonden
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default OrderDetailSheet;
